import { API_URL } from 'config';
import configureAPI from './shared/apiFactory';
import { readSseFrames } from './shared/sse';

const assistantApi = configureAPI(API_URL);

interface SubmitPromptParams {
  userId: string;
  coachId: string;
  prompt: string;
}

export const submitPromptStream = (
  params: SubmitPromptParams,
  signal?: AbortSignal
) => {
  return assistantApi.post('assistant/prompt', {
    json: params,
    signal,
    timeout: false,
  });
};

export const transcribeVoice = async (blob: Blob): Promise<string> => {
  const res = await assistantApi
    .post('assistant/transcribe', {
      body: blob,
      headers: { 'Content-Type': 'audio/wav' },
      timeout: 60_000,
    })
    .json<{ text: string }>();
  return res.text;
};

export interface AssistantStatusEvent {
  sessionId: string;
  message: string;
  name: string;
  status: 'progress' | 'complete';
  timestamp: number;
}

export interface AssistantChunkEvent {
  sessionId: string;
  delta: string;
}

export interface AssistantCompleteEvent {
  sessionId: string;
  assistantMessageId: number;
}

export interface AssistantErrorEvent {
  sessionId: string;
  error: string;
}

const ASSISTANT_EVENT_TYPES = [
  'assistant:status',
  'assistant:chunk',
  'assistant:complete',
  'assistant:error',
] as const;

export type AssistantStreamEvent =
  | { type: 'assistant:status'; data: AssistantStatusEvent }
  | { type: 'assistant:chunk'; data: AssistantChunkEvent }
  | { type: 'assistant:complete'; data: AssistantCompleteEvent }
  | { type: 'assistant:error'; data: AssistantErrorEvent };

export async function* readAssistantStream(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<AssistantStreamEvent> {
  for await (const { event, data } of readSseFrames(body)) {
    if (ASSISTANT_EVENT_TYPES.includes(event as never)) {
      yield { type: event, data: JSON.parse(data) } as AssistantStreamEvent;
    }
  }
}
